import { BRAND_SLUG } from '@/_utils/Constants'

const SLUG_TO_BRAND_ID: Record<string, string> = Object.fromEntries(
  Object.entries(BRAND_SLUG).map(([id, slug]) => [slug, id]),
)

/** Return mapped slug for a shipper UUID, or the UUID if unmapped. */
export function getBrandSlug(shipperId: string | undefined | null): string {
  if (!shipperId) return ''
  return BRAND_SLUG[shipperId] ?? shipperId
}

/**
 * Resolve a brand path segment (slug or UUID) to a shipper UUID.
 * Unknown slugs / UUIDs are returned as-is.
 */
export function resolveBrandId(slugOrId: string | undefined | null): string {
  if (!slugOrId) return ''
  return SLUG_TO_BRAND_ID[slugOrId] ?? slugOrId
}

type BrandHrefOptions = {
  /** Path after /brand/{slug}, e.g. `/product/foo`, `/checkout`, `/reserve` */
  path?: string
  query?: Record<string, string | undefined | null> | string
  hash?: string
}

function buildQuery(query?: BrandHrefOptions['query']): string {
  if (!query) return ''
  if (typeof query === 'string') {
    const trimmed = query.replace(/^\?/, '')
    return trimmed ? `?${trimmed}` : ''
  }
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value != null && value !== '') params.set(key, value)
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/**
 * Build a brand URL from shipper UUID (uses slug when mapped).
 * @example getBrandHref(id) → `/brand/yaponamama`
 * @example getBrandHref(id, { path: '/checkout' }) → `/brand/yaponamama/checkout`
 * @example getBrandHref(id, { path: '/reserve', query: { branch_id: 'x' } })
 */
export function getBrandHref(
  shipperId: string | undefined | null,
  options?: BrandHrefOptions,
): string {
  const slug = getBrandSlug(shipperId)
  if (!slug) return '#'

  const suffix = options?.path
    ? options.path.startsWith('/')
      ? options.path
      : `/${options.path}`
    : ''
  const query = buildQuery(options?.query)
  const hash = options?.hash
    ? options.hash.startsWith('#')
      ? options.hash
      : `#${options.hash}`
    : ''

  return `/brand/${slug}${suffix}${query}${hash}`
}

type WhereToGoHrefOptions = {
  /** Path after /where-to-go/{brand}/{branch}, e.g. `/search` */
  path?: string
  query?: BrandHrefOptions['query']
}

/**
 * Build a where-to-go branch URL from shipper UUID + branch slug (or id).
 * @example getWhereToGoHref(id, 'chilonzor') → `/where-to-go/yaponamama/chilonzor`
 * @example getWhereToGoHref(id, 'chilonzor', { path: '/search' })
 */
export function getWhereToGoHref(
  shipperId: string | undefined | null,
  branchSlugOrId: string | undefined | null,
  options?: WhereToGoHrefOptions,
): string {
  const brandSlug = getBrandSlug(shipperId)
  const branchSegment = branchSlugOrId || ''
  if (!brandSlug || !branchSegment) return '#'

  const suffix = options?.path
    ? options.path.startsWith('/')
      ? options.path
      : `/${options.path}`
    : ''
  const query = buildQuery(options?.query)

  return `/where-to-go/${brandSlug}/${branchSegment}${suffix}${query}`
}
